import { memo, useEffect, useMemo, useState } from 'react';
import type { PublicView } from '@ringmaster/shared';
import { BroadcastFrame } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { PlayerRail } from './components/index.js';
import { ParticleLayer } from '../fx/index.js';
import { Glyph } from '../ui/Glyph.js';
import { ornaments } from './ornaments.js';

/**
 * Stage DayScreen — the daytime discussion (DISCUSS phase). A warm, lantern-lit
 * town square: the living stand as a PlayerRail, a large discussion countdown
 * ticks above them, gold dust drifts. The host's "end discussion" control is
 * host/hand chrome, NOT part of this broadcast surface — the back-compat shim
 * accepts it and ignores it here.
 *
 * Two exports: DayScreenContent (inner) + DayScreenDemo (self-framed for CardLab).
 */

export interface DayScreenProps {
  view: PublicView;
  isHost: boolean;
}

function fmt(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function DayScreenContentImpl({ view }: DayScreenProps) {
  const living = useMemo(
    () => view.players.filter((p) => p.alive).map((p) => ({ id: p.id, name: p.name, alive: p.alive, avatarUrl: p.avatarUrl, discordUserId: p.discordUserId })),
    [view.players],
  );

  // Local countdown clock (server-driven deadline).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (view.timerEndsAt == null || view.paused) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [view.timerEndsAt, view.paused]);

  const frozen = view.paused ? view.timerRemainingMs : null;
  const hasTimer = frozen != null || view.timerEndsAt != null;
  const left = frozen ?? (view.timerEndsAt != null ? view.timerEndsAt - now : 0);
  const urgent = !view.paused && left > 0 && left <= 30_000;

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <ParticleLayer preset="goldDust" intensity={0.35} slot={2} />

      {/* Header: phase + big gold countdown — the hero of the discussion phase.
          Positioned fully BELOW the proscenium valance (which covers the top ~16% of
          the 1080 design frame) so the sign is never clipped by the pelmet art. */}
      <div
        style={{
          position: 'absolute',
          top: '19%',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          zIndex: 3,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 'var(--stage-text-meta, 24px)',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--gold-4, #c9923f)',
          }}
        >
          <Glyph name="scales" tone="gold" size={26} /> Day {view.round} · Discussion
        </div>
        {hasTimer && (
          <div
            style={{
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontSize: 'var(--stage-text-title, 96px)',
              fontWeight: 700,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: view.paused
                ? 'var(--text-tertiary, rgba(255,255,255,0.5))'
                : urgent
                  ? 'var(--blood-6, #ff5a4f)'
                  : 'var(--gold-6, #f7e3b0)',
              textShadow: urgent ? 'var(--rim-blood, 0 0 24px rgba(153,0,0,.55))' : 'none',
            }}
          >
            {fmt(left)}
          </div>
        )}
      </div>

      {/* Living roster as the standing crowd. */}
      <PlayerRail players={living} bustSize={140} />
    </div>
  );
}

export const DayScreenContent = memo(DayScreenContentImpl);

/** CardLab / standalone — full frame stack (DAY scene). Passes the proscenium
 *  ornaments so this preview matches the live StageView. */
export function DayScreenDemo({ view, isHost }: DayScreenProps) {
  const manifest = scenesForGame('bloodmoon');
  const inner = <DayScreenContent view={view} isHost={isHost} />;
  const framed = (
    <ProsceniumFrame
      cornerSrc={ornaments.filigreeCorner}
      grainSrc={ornaments.grainTile}
    >
      {inner}
    </ProsceniumFrame>
  );
  return (
    <BroadcastFrame>
      {manifest ? (
        <SceneStack manifest={manifest} phase={view.phase}>
          {framed}
        </SceneStack>
      ) : (
        framed
      )}
    </BroadcastFrame>
  );
}
